import type * as React from "react"

import { cn } from "@/lib/utils"

export interface TimeSelectProps
  extends Omit<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    "children"
  > {
  /**
   * Earliest selectable time (inclusive), 24h HH:mm.
   * Defaults to 06:00 (6:00am).
   */
  start?: string
  /**
   * Latest selectable time (inclusive), 24h HH:mm.
   * Defaults to 20:00 (8:00pm).
   */
  end?: string
  /**
   * Step in minutes between options.
   * Defaults to 15.
   */
  stepMinutes?: number
}

function parseTimeToMinutes(value: string | undefined): number | null {
  if (!value) return null
  const [hh, mm] = value.split(":")
  const hours = Number(hh)
  const minutes = Number(mm)
  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null
  }
  return hours * 60 + minutes
}

function minutesToValue(totalMinutes: number): string {
  const minutesInDay = 24 * 60
  const normalised =
    ((totalMinutes % minutesInDay) + minutesInDay) % minutesInDay
  const hours = Math.floor(normalised / 60)
  const minutes = normalised % 60
  const hh = String(hours).padStart(2, "0")
  const mm = String(minutes).padStart(2, "0")
  return `${hh}:${mm}`
}

function minutesToLabel(totalMinutes: number): string {
  const minutesInDay = 24 * 60
  const normalised =
    ((totalMinutes % minutesInDay) + minutesInDay) % minutesInDay
  const hours24 = Math.floor(normalised / 60)
  const minutes = normalised % 60
  const isPm = hours24 >= 12
  const hours12 = hours24 % 12 || 12
  const mm = String(minutes).padStart(2, "0")
  const suffix = isPm ? "pm" : "am"
  return `${hours12}:${mm}${suffix}`
}

export function TimeSelect(props: TimeSelectProps) {
  const {
    start = "06:00",
    end = "20:00",
    stepMinutes = 15,
    className,
    ...rest
  } = props
  const isReadOnlyLike = Boolean(rest.disabled)

  const startMinutes = parseTimeToMinutes(start) ?? 6 * 60
  const endMinutes = parseTimeToMinutes(end) ?? 20 * 60

  const effectiveStep = stepMinutes > 0 ? stepMinutes : 15

  const from = Math.min(startMinutes, endMinutes)
  const to = Math.max(startMinutes, endMinutes)

  const options: { value: string; label: string }[] = []
  for (let minutes = from; minutes <= to; minutes += effectiveStep) {
    options.push({
      value: minutesToValue(minutes),
      label: minutesToLabel(minutes),
    })
  }

  return (
    <select
      {...rest}
      className={cn(
        "jc-no-ios-zoom",
        "w-full text-sm text-slate-900 outline-none",
        isReadOnlyLike
          ? "rounded-md bg-slate-100 px-2 py-1 text-slate-600 shadow-[0_1px_0_0_rgba(203,213,225,1)]"
          : "bg-transparent shadow-[0_1px_0_0_rgba(148,163,184,1)] focus:shadow-[0_1px_0_0_rgba(15,23,42,1)]",
        className,
      )}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  )
}
